import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  IconBuildingBank,
  IconCreditCard,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import RadioButtonOption from "../../../../../../../components/forms/RadioButtonOption";
import Button from "../../../../../../../components/ui/Button";
import Input from "../../../../../../../components/ui/Input";
import Text from "../../../../../../../components/ui/Text";
import { GAME_PAYMENT_MODE } from "../../../../../../../constants/game";
import { useAuth } from "../../../../../../../contexts/AuthContext";
import { useToast } from "../../../../../../../contexts/ToastContext";
import { StripeConnectDialog } from "./parts";

const PaymentCollection = ({
  gameData,
  onPlayerAmountChange,
  onRevolutToggle,
  onRevolutLinkChange,
  onGameDataChange,
  readOnly = false,
}) => {
  const { showToast } = useToast();
  const { currentUser, fetchUserProfile } = useAuth();
  const isStripeConnected = currentUser?.stripe_connected;

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [isServiceFeeModalOpen, setIsServiceFeeModalOpen] = useState(false);
  const [isStripeDialogOpen, setIsStripeDialogOpen] = useState(false);

  // Use refs to hold the latest gameData and onGameDataChange
  const gameDataRef = useRef(gameData);
  const onGameDataChangeRef = useRef(onGameDataChange);

  // Update refs on each render
  useEffect(() => {
    gameDataRef.current = gameData;
    onGameDataChangeRef.current = onGameDataChange;
  });

  const handlePaymentModeChange = useCallback((mode) => {
    onGameDataChangeRef.current({
      ...gameDataRef.current,
      payment_mode: mode,
      ...(mode === GAME_PAYMENT_MODE.IN_APP
        ? { use_revolut: false, revolut_link: "" }
        : {}),
    });
  }, []); // Empty dependency array - function never changes

  useEffect(() => {
    // Set up BroadcastChannel to listen for Stripe connection messages
    const channel = new BroadcastChannel("stripe_connection");

    channel.onmessage = async (event) => {
      if (event.data.type === "STRIPE_CONNECTED" && event.data.success) {
        showToast(
          event.data.message || "Stripe account connected successfully!",
          "success",
        );
        if (currentUser?.accessToken) {
          await fetchUserProfile(currentUser.accessToken);
        }
        // Use stable function to set payment mode
        handlePaymentModeChange(GAME_PAYMENT_MODE.IN_APP);
        setIsStripeDialogOpen(false);
      }
    };

    return () => {
      channel.close();
    };
  }, [
    showToast,
    currentUser?.accessToken,
    fetchUserProfile,
    handlePaymentModeChange,
  ]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Text variant="subtitle3">Payment collection</Text>
      </Box>

      {readOnly && (
        <Box
          sx={{
            backgroundColor: "#FFF7E6",
            border: "1px solid #FFD666",
            borderRadius: "6px",
            p: 2,
            mb: 2,
          }}
        >
          <Text variant="paragraph3" sx={{ color: "#874D00" }}>
            A player has already paid to join this game, so payment details are
            locked to avoid a mismatch with what they were charged.
          </Text>
        </Box>
      )}

      <RadioGroup value={gameData.payment_mode}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isDesktop ? "row" : "column",
            gap: 2,
          }}
        >
          {/* Collect payment in app option */}
          <Box sx={{ flex: isDesktop ? 1 : "none" }}>
            <RadioButtonOption
              disabled={readOnly || !isStripeConnected}
              value={GAME_PAYMENT_MODE.IN_APP}
              selectedValue={gameData.payment_mode}
              icon={<IconBuildingBank size={16} />}
              title="Collect payment in app"
              description="Players will need to pay to join. Get paid into your bank account after the game."
              onClick={(value) => {
                if (readOnly) return;
                if (isStripeConnected) {
                  handlePaymentModeChange(value);
                } else {
                  handlePaymentModeChange("");
                  setIsStripeDialogOpen(true);
                }
              }}
            />
          </Box>

          {/* I'll collect payment myself option */}
          <Box sx={{ flex: isDesktop ? 1 : "none" }}>
            <RadioButtonOption
              disabled={readOnly}
              value={GAME_PAYMENT_MODE.SELF}
              selectedValue={gameData.payment_mode}
              icon={<IconCreditCard size={16} />}
              title="I'll collect payment myself"
              description="Players can join upfront and you will be responsible for collecting payment."
              onClick={(value) => !readOnly && handlePaymentModeChange(value)}
            />
          </Box>
        </Box>
      </RadioGroup>
      {/* Payment Fields - Show below both options */}
      {gameData.payment_mode === GAME_PAYMENT_MODE.IN_APP && (
        <Box sx={{ mt: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isDesktop ? "row" : "column",
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Input
                disabled={readOnly}
                label="Set the amount to receive from each player"
                value={gameData.playerAmount}
                onChange={onPlayerAmountChange}
                prefix="€"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Input
                disabled
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    Total players pay to join (includes service fee)
                    <IconButton
                      onClick={() => setIsServiceFeeModalOpen(true)}
                      size="small"
                      sx={{ p: 0, color: "#A1A1A1" }}
                    >
                      <IconInfoCircle size={16} />
                    </IconButton>
                  </Box>
                }
                sx={{
                  "& .MuiInputBase-root.Mui-disabled": {
                    color: "#000 !important",
                  },
                }}
                value={gameData.totalAmount}
                prefix="€"
              />
            </Box>
          </Box>

          {/* Refund Policy Section */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Text
                variant="paragraph"
                sx={{ fontSize: "14px", fontWeight: 500 }}
              >
                Do you want to allow refunds?
              </Text>
              <IconButton
                onClick={() => setIsServiceFeeModalOpen(true)}
                size="small"
                sx={{ p: 0, color: "#A1A1A1" }}
              >
                <IconInfoCircle size={16} />
              </IconButton>
            </Box>

            <RadioGroup
              value={gameData.refund_policy || "NO_REFUNDS"}
              onChange={(e) => {
                const value = e.target.value;
                const cutoffHours =
                  value === "NO_REFUNDS"
                    ? 0
                    : value === "REFUND_8_HOURS"
                      ? 8
                      : value === "REFUND_24_HOURS"
                        ? 24
                        : gameData.refund_cutoff_hours || "";
                onGameDataChange({
                  ...gameData,
                  refund_policy: value,
                  refund_cutoff_hours: cutoffHours,
                });
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <FormControlLabel
                  disabled={readOnly}
                  value="NO_REFUNDS"
                  control={
                    <Radio
                      sx={{
                        color: "#D0D0D0",
                        "&.Mui-checked": {
                          color: "#1990FF",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  label={
                    <Text variant="paragraph3" sx={{ fontSize: "14px" }}>
                      No refunds
                    </Text>
                  }
                  sx={{ ml: 0 }}
                />
                <FormControlLabel
                  disabled={readOnly}
                  value="REFUND_8_HOURS"
                  control={
                    <Radio
                      sx={{
                        color: "#D0D0D0",
                        "&.Mui-checked": {
                          color: "#1990FF",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  label={
                    <Text variant="paragraph3" sx={{ fontSize: "14px" }}>
                      Allow refunds 8 hours before game start time
                    </Text>
                  }
                  sx={{ ml: 0 }}
                />
                <FormControlLabel
                  disabled={readOnly}
                  value="REFUND_24_HOURS"
                  control={
                    <Radio
                      sx={{
                        color: "#D0D0D0",
                        "&.Mui-checked": {
                          color: "#1990FF",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  label={
                    <Text variant="paragraph3" sx={{ fontSize: "14px" }}>
                      Allow refunds 24 hours before game start time
                    </Text>
                  }
                  sx={{ ml: 0 }}
                />
                <FormControlLabel
                  disabled={readOnly}
                  value="CUSTOM_CUTOFF"
                  control={
                    <Radio
                      sx={{
                        color: "#D0D0D0",
                        "&.Mui-checked": {
                          color: "#1990FF",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  label={
                    <Text variant="paragraph3" sx={{ fontSize: "14px" }}>
                      Set custom cut-off time for refunds.
                    </Text>
                  }
                  sx={{ ml: 0 }}
                />
              </Box>
            </RadioGroup>

            {gameData.refund_policy === "CUSTOM_CUTOFF" && (
              <Box sx={{ mt: 2 }}>
                <Input
                  disabled={readOnly}
                  label="Custom cut-off time (Hours)"
                  type="number"
                  value={gameData.refund_cutoff_hours || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    onGameDataChange({
                      ...gameData,
                      refund_cutoff_hours: value ? parseInt(value, 10) : "",
                    });
                  }}
                  placeholder="Enter number of hours"
                  inputProps={{ min: 1, max: 168 }}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {gameData.payment_mode === GAME_PAYMENT_MODE.SELF && (
        <Box sx={{ mt: 3 }}>
          <Input
            disabled={readOnly}
            label="Set the amount to receive from each player"
            value={gameData.playerAmount}
            onChange={onPlayerAmountChange}
            prefix="€"
            sx={{ mb: 2 }}
          />

          {/* Revolut checkbox and link */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              my: 2,
            }}
          >
            <input
              type="checkbox"
              checked={gameData.use_revolut}
              onChange={onRevolutToggle}
              disabled={readOnly}
              style={{
                width: "16px",
                height: "16px",
                accentColor: "#1990FF",
              }}
            />
            <Text variant="paragraph3" sx={{ fontWeight: 500 }}>
              Get paid through revolut
            </Text>
          </Box>
          {gameData.use_revolut && (
            <Input
              disabled={readOnly}
              label="Revolut payment link"
              value={gameData.revolut_link}
              onChange={onRevolutLinkChange}
              placeholder="revolut.me/yourcode"
            />
          )}
        </Box>
      )}

      {/* Stripe Info Modal */}
      <StripeConnectDialog
        open={isStripeDialogOpen}
        onClose={() => setIsStripeDialogOpen(false)}
      />

      {/* Service Fee Info Modal */}
      <Dialog
        open={isServiceFeeModalOpen}
        onClose={() => setIsServiceFeeModalOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { borderRadius: "12px", m: 2, width: "310px" } },
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 1 }}>
          <Text variant="pageHeader" sx={{ fontSize: "20px" }}>
            Service fee
          </Text>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Text variant="paragraph" sx={{ fontSize: "16px", lineHeight: 1.5 }}>
            The service fee is 20% of the total order value. It has a minimum of
            €0.80.
          </Text>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            variant="secondary"
            onClick={() => setIsServiceFeeModalOpen(false)}
            sx={{ width: "auto" }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentCollection;
